"use client";

import React, { useState } from "react";
import Image from "next/image";
import ScrollReveal from "@/components/common/ScrollReveal";
import TabbedContent from "@/components/common/TabbedContent";
import RingOrbit, { type OrbitDot } from "@/components/common/RingOrbit";

type ServiceId = "consulting" | "internal-audit" | "external-audit" | "caas";

/**
 * Figma node 684:48451 — the 853×792 box the circuit texture fills. The
 * diagram artwork is cut to the same box, so hotspots are plain percentages.
 */
const BOX_W = 853;
const BOX_H = 792;

interface Ring {
  /** Where the ring's Figma frame sits in the box. */
  x: number;
  y: number;
  /** Track centre in the ring's frame: the orbit's pivot and the hotspot's centre. */
  cx: number;
  cy: number;
  /** Hotspot radius in box units, wide enough to take the curved label too. */
  hit: number;
  /** The small red rings on the track, in the ring's frame. Always turning. */
  dots: OrbitDot[];
  dotWidth: number;
  /**
   * The outermost arc(s), in the ring's frame. They turn red while the ring's
   * tab is selected. External Audit's and Consulting's are outlined in Figma;
   * these are their centre lines, stroked at the outline's 1.5px.
   */
  arcs: string[];
  arcWidth: number;
  idle: string;
}

/**
 * The parts of each ring that move, cut out of core-services-diagram.svg; the
 * rest of the artwork stays a static image underneath.
 */
const RINGS: Record<ServiceId, Ring> = {
  "internal-audit": {
    x: 295.86,
    y: 28.43,
    cx: 80.64,
    cy: 120.47,
    hit: 70,
    dots: [
      { x: 46.51, y: 73.19, r: 3.65 },
      { x: 137.68, y: 115.98, r: 3.65 },
      { x: 47.09, y: 167.94, r: 3.65 },
    ],
    dotWidth: 1.5,
    arcs: [
      "M2.55598 103.293C-1.2525 119.215 1.22209 136.65 7.546 151.714C34.6531 208.248 91.7916 203.489 118.273 189.169C140.342 177.236 166.505 143.161 154.457 101.156",
    ],
    arcWidth: 1.5,
    idle: "var(--color-diagram-line)",
  },
  "external-audit": {
    x: 489.45,
    y: 126,
    cx: 85.26,
    cy: 88.88,
    hit: 72,
    dots: [
      { x: 128.39, y: 51.98, r: 3.58 },
      { x: 95.37, y: 144.84, r: 3.46 },
      { x: 36.05, y: 60.87, r: 3.58 },
    ],
    dotWidth: 1.5,
    arcs: [
      "M138.052 30.127C150.41 40.79 158.55 56.24 162.174 71.984C167.61 102.48 158.03 124.9 143.36 140.15C128.69 155.39 108.93 163.45 94.005 165.224C69.13 168.18 28.05 155.62 13.392 114.914",
    ],
    arcWidth: 1.5,
    idle: "var(--color-diagram-line)",
  },
  consulting: {
    x: 550.48,
    y: 306.03,
    cx: 104.81,
    cy: 103.82,
    hit: 95,
    dots: [
      { x: 39.42, y: 150.5, r: 5.07 },
      { x: 97.93, y: 24.91, r: 5.07 },
      { x: 170.28, y: 150.43, r: 5.07 },
    ],
    dotWidth: 1.5,
    arcs: [
      "M130.592 4.796C88.44 -6.53 40.45 11.96 18.153 48.418C-12.7 96.48 6.35 147.92 26.495 170.431C38.16 183.47 73.22 215.67 126.329 203.86",
    ],
    arcWidth: 1.5,
    // Figma's #3D3F46, a hair off the token's #3C4451.
    idle: "var(--color-diagram-track)",
  },
  caas: {
    x: 422.5,
    y: 496,
    cx: 100.75,
    cy: 94.06,
    hit: 80,
    dots: [
      { x: 176.62, y: 88.15, r: 6.3 },
      { x: 88.11, y: 168.55, r: 6.3 },
      { x: 50.17, y: 37.05, r: 6.3 },
    ],
    dotWidth: 2,
    arcs: [
      "M144.611 178.454C144.464 178.529 144.317 178.6 144.171 178.674C131.275 185.098 116.702 188.717 101.271 188.717C63.578 188.717 30.997 167.128 15.54 135.8",
      "M174.93 35.06C188.446 51.288 196.56 72.061 196.56 94.704C196.56 112.877 191.333 129.844 182.281 144.225C182.205 144.347 182.129 144.468 182.053 144.59",
    ],
    arcWidth: 2,
    idle: "var(--color-diagram-line)",
  },
};

const bullet = (
  <span
    aria-hidden
    // Centred on the first line of the accordion/tab copy (24/26/28px).
    className="mt-2 size-2 shrink-0 rounded-full bg-primary sm:mt-[9px] lg:mt-[10px]"
  />
);

const CAAS_ITEMS: { term: string; text: string }[][] = [
  [
    {
      term: "Monthly support",
      text: "Continuous control monitoring, evidence collection, policy maintenance, surveillance-audit preparation, and proactive regulatory-change watch.",
    },
    {
      term: "Security Assessment",
      text: "Vulnerability assessment, penetration testing, and red-team validation — feeding real-world evidence straight into your compliance program.",
    },
    {
      term: "IT Governance Solution",
      text: "Governance frameworks, risk registers, and board- and management-level reporting.",
    },
  ],
  [
    {
      term: "Security Compliance Solution",
      text: "Tools and processes that operationalize controls and sustain continuous compliance with less manual effort.",
    },
    {
      term: "LMS for IT & Compliance Training",
      text: "Role-based security-awareness and compliance training, with completion tracking and audit-ready reporting.",
    },
    {
      term: "Compliance Platform (Compliance & ITSM)",
      text: "A single platform to manage multiple frameworks, map shared controls once, and align compliance with IT service management (ITSM).",
    },
  ],
];

const SERVICES = [
  {
    id: "consulting",
    title: ["Consulting", "Services"],
    content: (
      <p>
        ECQ’s consultants guide organizations from gap to certification across any standard pursued,
        including ISO 27001, PCI DSS, SOC 2. We assess the organization’s current standing, design
        the controls and management system required, and lead remediation through to audit
        readiness. Because our consultants work in close partnership with our technical team, effort
        is directed where real risk resides, ensuring clients do not merely meet the standard but
        measurably strengthen their security posture.
      </p>
    ),
  },
  {
    id: "internal-audit",
    title: "Internal Audit",
    content: (
      <p>
        Before the formal audit, ECQ provides an independent, expert assessment of the
        organization’s true standing. We evaluate control effectiveness, review evidence, and report
        to management in clear terms: what is working, what requires attention, and how to close the
        gap. Offered on a co-sourced or fully outsourced basis, our internal audit eliminates the
        risk of surprises when the certification or regulatory audit arrives.
      </p>
    ),
  },
  {
    id: "external-audit",
    title: "External Audit",
    // Figma (node 2759:39705) repeats the Internal Audit copy word for word
    // here; it ships as designed until External Audit gets its own text.
    content: (
      <p>
        Before the formal audit, ECQ provides an independent, expert assessment of the
        organization’s true standing. We evaluate control effectiveness, review evidence, and report
        to management in clear terms: what is working, what requires attention, and how to close the
        gap. Offered on a co-sourced or fully outsourced basis, our internal audit eliminates the
        risk of surprises when the certification or regulatory audit arrives.
      </p>
    ),
  },
  {
    id: "caas",
    title: ["Compliance", "As A Service"],
    content: (
      <div className="flex flex-col gap-4 lg:gap-2">
        <p>
          Compliance is not a one-time undertaking; it is a standard that must be sustained. ECQ’s
          Compliance-as-a-Service keeps organizations audit-ready throughout the year, particularly
          for obligations that demand continuous upkeep such as PDPA, GDPR, and ISO 27001,
          safeguarding both certification and reputation between audit cycles.
        </p>
        <div className="flex flex-col gap-1 lg:flex-row lg:gap-6">
          {CAAS_ITEMS.map((column, index) => (
            <ul key={index} className="flex flex-1 flex-col gap-1">
              {column.map((item) => (
                <li key={item.term} className="flex gap-4">
                  {bullet}
                  <span>
                    <span className="font-semibold">{item.term}:</span> {item.text}
                  </span>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>
    ),
  },
] satisfies { id: ServiceId; title: string | string[]; content: React.ReactNode }[];

/**
 * Security Compliance › Core Services — Figma node 684:45841, with the tab
 * panel from the "Core Capabilities" component set (node 2759:39621).
 *
 * The four rings in the artwork select the matching tab, as the circles did on
 * the Local Regulatory page this one replaces, and the selected ring's outermost
 * arc turns red — the same cue as Standard Delivers. Figma draws no selected
 * state here; every other part of an unselected ring stays as drawn. The small
 * red rings on every track turn regardless. The breadcrumb, title and tab row
 * come from the shared defend layout.
 */
const CoreServices = () => {
  const [activeId, setActiveId] = useState<ServiceId>("consulting");

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2">
      <div className="mx-auto flex w-full max-w-[1300px] flex-col items-center gap-8 lg:gap-12">
        <ScrollReveal direction="up" delay={0} className="w-full">
          <div
            className="relative mx-auto w-full max-w-[853px]"
            style={{
              aspectRatio: `${BOX_W} / ${BOX_H}`,
              containerType: "inline-size",
            }}
          >
            <Image
              src="/images/Compliance/core-services-circuit.png"
              alt=""
              fill
              sizes="(min-width: 900px) 853px, 100vw"
              className="pointer-events-none object-cover opacity-20"
            />
            <Image
              src="/images/Compliance/core-services-diagram.svg"
              alt=""
              fill
              className="pointer-events-none"
            />
            <svg
              viewBox={`0 0 ${BOX_W} ${BOX_H}`}
              fill="none"
              className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
              aria-hidden
            >
              {SERVICES.map(({ id }) => {
                const ring = RINGS[id];
                return (
                  <g key={id} transform={`translate(${ring.x} ${ring.y})`}>
                    {ring.arcs.map((d) => (
                      <path
                        key={d}
                        d={d}
                        strokeWidth={ring.arcWidth}
                        strokeLinecap="round"
                        className="transition-[stroke] duration-300 motion-reduce:transition-none"
                        style={{
                          stroke: id === activeId ? "var(--color-primary)" : ring.idle,
                        }}
                      />
                    ))}
                    <RingOrbit
                      cx={ring.cx}
                      cy={ring.cy}
                      dots={ring.dots}
                      strokeWidth={ring.dotWidth}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Pointer shortcuts onto the tabs below, which stay the keyboard
                and screen-reader route — hence hidden from both. */}
            {SERVICES.map(({ id }) => {
              const ring = RINGS[id];
              const x = ring.x + ring.cx;
              const y = ring.y + ring.cy;
              const r = ring.hit;
              return (
                <button
                  key={id}
                  type="button"
                  tabIndex={-1}
                  aria-hidden
                  onClick={() => setActiveId(id)}
                  className="absolute flex cursor-pointer items-center justify-center rounded-full text-center font-bold leading-[1.35] text-primary"
                  style={{
                    left: `${((x - r) / BOX_W) * 100}%`,
                    top: `${((y - r) / BOX_H) * 100}%`,
                    width: `${((2 * r) / BOX_W) * 100}%`,
                    height: `${((2 * r) / BOX_H) * 100}%`,
                    // 18px at the full 853px box.
                    fontSize: "clamp(8px, 2.1102cqw, 18px)",
                  }}
                >
                  {/* The only ring label that is live text in Figma; the
                      other three are outlined into the artwork. */}
                  {id === "caas" && (
                    <span>
                      Compliance
                      <br />
                      As A Service
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={150} className="w-full">
          <TabbedContent
            items={SERVICES}
            currentTab={activeId}
            onTabChange={(id) => setActiveId(id as ServiceId)}
            label="Core services"
            // Mobile lands on Consulting Services open, like desktop does.
            defaultOpenRow="consulting"
          />
        </ScrollReveal>
      </div>
    </section>
  );
};

export default CoreServices;
